import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaChevronDown } from 'react-icons/fa';

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  border-radius: 3px;
  border: 1px solid #3b424a;
  background-color: #2f343a;
  color: #e0f0ff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #0073e6;
  }

  &:focus {
    border-color: #0073e6;
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 115, 230, 0.1);
  }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background-color: #2f343a;
  border-radius: 3px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  display: ${props => props.isOpen ? 'block' : 'none'};
  
  /* Customize scrollbar for dark theme */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #1a1d23;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #0073e6;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #0062cc;
  }
`;

const DropdownItem = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  color: #e0f0ff;
  
  &:hover {
    background-color: #3b424a;
  }
`;

const SelectedValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
`;

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  transition: transform 0.2s;
  color: #a0c0e0;
`;

const ModelNameInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border-radius: 3px;
  border: 1px solid #3b424a;
  background-color: #2f343a;
  color: #e0f0ff;
  font-size: 14px;
  &:focus {
    border-color: #0073e6;
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 115, 230, 0.1);
  }
  &::placeholder {
    color: #a0c0e0;
    opacity: 0.7;
  }
`;

const ModelNameDropdown = ({ value, onChange, provider, required }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const dropdownRef = useRef(null);
  
  // Model options by provider
  const modelOptions = {
    openai: [
      'gpt-4o',
      // 'gpt-4-turbo',
      // 'gpt-4',
      // 'gpt-3.5-turbo',
      // 'text-embedding-3-large',
      // 'text-embedding-3-small',
      // 'dall-e-3'
    ],
    google: [
      'gemini-1.5-pro',
      'gemini-1.5-flash',
      'gemini-pro',
      'gemini-pro-vision',
      'text-embedding-gecko'
    ],
    anthropic: [
      'claude-3-opus',
      'claude-3-sonnet',
      'claude-3-haiku',
      'claude-2',
      'claude-instant'
    ],
    groq: [
      'llama2-70b',
      'llama3-70b',
      'mixtral-8x7b',
      'gemma-7b'
    ]
  };
  
  // Get models for current provider
  const getModels = () => {
    return modelOptions[provider] || [];
  };
  
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (isCustom) {
      setIsCustom(false);
    }
  };
  
  const handleSelect = (model) => {
    onChange(model);
    setIsOpen(false);
  };
  
  const handleCustomOption = () => {
    setIsCustom(true);
    setIsOpen(false);
  };
  
  const handleCustomInputChange = (e) => {
    setCustomValue(e.target.value);
  };
  
  const handleCustomInputBlur = () => {
    if (customValue.trim()) {
      onChange(customValue.trim());
    }
    setIsCustom(false);
  };
  
  const handleCustomInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (customValue.trim()) {
        onChange(customValue.trim());
      }
      setIsCustom(false);
    }
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsCustom(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Update custom value when value changes externally
  useEffect(() => {
    setCustomValue(value || '');
  }, [value]);
  
  const models = getModels();
  
  return (
    <DropdownContainer ref={dropdownRef}>
      {isCustom ? (
        <ModelNameInput
          type="text"
          value={customValue}
          onChange={handleCustomInputChange}
          onBlur={handleCustomInputBlur}
          onKeyDown={handleCustomInputKeyDown}
          autoFocus
          placeholder="Enter custom model name"
          required={required}
        />
      ) : (
        <>
          <DropdownButton 
            type="button" 
            onClick={toggleDropdown}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            <SelectedValue>
              {value || 'Select a model'}
            </SelectedValue>
            <ChevronIcon isOpen={isOpen}>
              <FaChevronDown />
            </ChevronIcon>
          </DropdownButton>
          
          <DropdownContent isOpen={isOpen} role="listbox">
            {models.map(model => (
              <DropdownItem 
                key={model}
                onClick={() => handleSelect(model)}
                role="option"
                aria-selected={value === model}
              >
                {model}
              </DropdownItem>
            ))}
            <DropdownItem 
              onClick={handleCustomOption}
              role="option"
              style={{ borderTop: '1px solid #f0f0f0', fontStyle: 'italic' }}
            >
              Custom model name...
            </DropdownItem>
          </DropdownContent>
        </>
      )}
    </DropdownContainer>
  );
};

export default ModelNameDropdown;
